/**
 * AgentStateContext
 *
 * React context that coordinates the Platform Assistant UI state system.
 * Provides element/action registration and UI control logic.
 *
 * The context does NOT manage its own WebSocket connection. Instead, it exposes
 * UI control handlers that can be used by the voice chat hook (usePlatformAssistantChat)
 * which manages the unified WebSocket connection for both voice and UI control.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
  ReactNode,
} from 'react';

import { AgentElementRegistry } from '../services/AgentElementRegistry';
import { AgentActionRegistry } from '../services/AgentActionRegistry';
import { AgentHighlightManager } from '../services/AgentHighlightManager';
import { UIControlHandlerRegistry } from '../services/UIControlHandlerRegistry';
import type {
  AgentStateContextValue,
  ElementRegistration,
  ActionRegistration,
  UIState,
  ActionResult,
} from '../types';
import type { UIControlHandlers } from '../hooks/usePlatformAssistantChat';

// ============================================
// EXTENDED CONTEXT TYPE
// ============================================

/**
 * Extended context value that includes UI control handlers for voice chat integration
 */
export interface AgentStateContextValueExtended extends AgentStateContextValue {
  /** UI control handlers to pass to usePlatformAssistantChat */
  uiControlHandlers: UIControlHandlers;
}

// ============================================
// CONTEXT
// ============================================

const AgentStateContext = createContext<AgentStateContextValueExtended | null>(null);

// ============================================
// ASYNC COMPLETION POLLING
// ============================================

/**
 * Poll the element registry until async loading completes or navigation occurs.
 *
 * After actions that trigger async work (e.g., form submission that starts AI generation),
 * the UI state needs time to update. This function polls until the loading state resolves
 * by checking for elements with is_loading/is_streaming flags.
 *
 * Waits until one of:
 * 1. Navigation occurs (URL changed) - detected and handled by caller
 * 2. No elements are in a loading/streaming/generating state
 */
async function pollForAsyncCompletion(
  registry: AgentElementRegistry,
  initialUrl: string,
  maxWaitMs = 60000,
  pollIntervalMs = 500
): Promise<{ uiState: UIState; navigationOccurred: boolean }> {
  const startTime = Date.now();

  // Wait for the initial state transition (e.g., step change from 'form' to 'objectives')
  await new Promise((resolve) => setTimeout(resolve, 1000));

  while (Date.now() - startTime < maxWaitMs) {
    // Check if navigation occurred
    if (window.location.href !== initialUrl) {
      console.log('[AgentState] Navigation detected during polling');
      return { uiState: registry.computeUIState(), navigationOccurred: true };
    }

    const uiState = registry.computeUIState();

    // Check if any element is still in a loading/streaming/generating state
    const isElementLoading = (el: { data: Record<string, unknown>; children?: unknown[] }): boolean => {
      const d = el.data;
      return d.is_loading === true || d.is_streaming === true || d.is_validating === true || d.is_generating_lesson === true;
    };
    const hasLoadingElement = uiState.sections.some((section) =>
      section.children.some((element) => isElementLoading(element))
    );

    if (!hasLoadingElement) {
      return { uiState, navigationOccurred: false };
    }

    console.log('[AgentState] Still loading, polling again...');
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }

  console.warn('[AgentState] Async polling timed out, returning current state');
  return { uiState: registry.computeUIState(), navigationOccurred: window.location.href !== initialUrl };
}

// ============================================
// PROVIDER PROPS
// ============================================

interface AgentStateProviderProps {
  /** Page type identifier (e.g., 'lesson_command_center') */
  pageType: string;

  /** Optional page-specific ID (e.g., lesson ID) */
  pageId?: string;

  children: ReactNode;
}

// ============================================
// PROVIDER
// ============================================

export function AgentStateProvider({
  pageType,
  pageId,
  children,
}: AgentStateProviderProps) {
  // Construct page context
  const pageContext = pageId ? `${pageType}_${pageId}` : pageType;

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [highlightedElements, setHighlightedElements] = useState<Set<string>>(
    new Set()
  );

  // Refs for services (persist across renders)
  const elementRegistryRef = useRef<AgentElementRegistry | null>(null);
  const actionRegistryRef = useRef<AgentActionRegistry | null>(null);
  const highlightManagerRef = useRef<AgentHighlightManager | null>(null);
  const [, setRegistryGeneration] = useState(0);

  // Initialize services
  useEffect(() => {
    elementRegistryRef.current = new AgentElementRegistry(pageType);
    actionRegistryRef.current = new AgentActionRegistry();
    highlightManagerRef.current = new AgentHighlightManager();
    // Children register before this effect runs; re-render so they re-register into the new registries.
    setRegistryGeneration((generation) => generation + 1);

    return () => {
      elementRegistryRef.current?.destroy();
      highlightManagerRef.current?.destroy();
      actionRegistryRef.current?.clear();
    };
  }, [pageType]);

  // ========================================
  // UI CONTROL HANDLERS (for voice chat integration)
  // ========================================

  /**
   * Get current UI state - called by voice chat when backend requests UI state
   */
  const getUIState = useCallback((): UIState => {
    const registry = elementRegistryRef.current;

    if (!registry) {
      throw new Error('Element registry not initialized');
    }

    return registry.computeUIState();
  }, []);

  /**
   * Execute actions - called by voice chat when backend requests action execution
   */
  const executeActions = useCallback(
    async (
      actions: Array<{ action_id: string; params: Record<string, unknown> }>
    ): Promise<{ results: ActionResult[]; newUIState: UIState }> => {
      const actionRegistry = actionRegistryRef.current;
      const elementRegistry = elementRegistryRef.current;
      const highlightManager = highlightManagerRef.current;

      console.log('[AgentState] executeActions called', {
        actions,
        hasActionRegistry: !!actionRegistry,
        hasElementRegistry: !!elementRegistry,
        hasHighlightManager: !!highlightManager,
      });

      if (!actionRegistry || !elementRegistry || !highlightManager) {
        throw new Error('Services not initialized');
      }

      // Capture initial URL to detect navigation
      const initialUrl = window.location.href;
      console.log('[AgentState] Initial URL:', initialUrl);

      // Execute actions without spotlight — the action steps (scroll, hover, click)
      // already guide the user's eye, and the spotlight interferes with dynamically
      // rendered content like dropdowns that appear outside the original element rect.
      const results = await actionRegistry.executeMultiple(
        actions,
        // Before each action: no spotlight needed
        async () => {},
        // After each action: no cleanup needed
        async () => {}
      );

      console.log('[AgentState] Action results:', results);

      // Detect if navigation occurred (URL changed)
      const currentUrl = window.location.href;
      const navigationOccurred = currentUrl !== initialUrl;
      console.log('[AgentState] Navigation check:', { initialUrl, currentUrl, navigationOccurred });

      if (navigationOccurred) {
        console.log('[AgentState] Navigation detected, waiting 800ms for new page...');
        await new Promise((resolve) => setTimeout(resolve, 800));

        // After navigation, use the global registry to get UI state from NEW provider
        const newHandlers = UIControlHandlerRegistry.getHandlers();
        console.log('[AgentState] After wait - newHandlers available:', !!newHandlers, 'hasOnGetUIState:', !!newHandlers?.onGetUIState);

        if (newHandlers?.onGetUIState) {
          try {
            const newUIState = newHandlers.onGetUIState();
            console.log('[AgentState] New UI state from new handlers:', newUIState?.page_type, 'sections:', newUIState?.sections?.length);
            return { results, newUIState };
          } catch (err) {
            console.error('[AgentState] Error getting UI state from new handlers:', err);
          }
        }
      } else {
        // Check if any action triggers async loading (e.g., form submission, chat message, or
        // step transition that starts AI generation). Poll until loading completes.
        // start_generation triggers navigation to lesson page after async API + Pusher event
        const triggersAsyncLoading = actions.some((a) =>
          ['submit_form', 'continue_to_outline', 'send_message', 'start_generation'].includes(a.action_id)
        );

        if (triggersAsyncLoading) {
          console.log('[AgentState] Async loading action detected, polling for completion...');
          const { uiState: polledUIState, navigationOccurred: navDuringPoll } = await pollForAsyncCompletion(elementRegistry, initialUrl);

          // If navigation occurred during polling, get UI state from new page's provider
          if (navDuringPoll) {
            console.log('[AgentState] Navigation detected during async polling, waiting for new page...');
            await new Promise((resolve) => setTimeout(resolve, 800));

            const newHandlersAfterPoll = UIControlHandlerRegistry.getHandlers();
            if (newHandlersAfterPoll?.onGetUIState) {
              try {
                const newUIState = newHandlersAfterPoll.onGetUIState();
                console.log('[AgentState] New UI state from new handlers (after poll navigation):', newUIState?.page_type, 'sections:', newUIState?.sections?.length);
                return { results, newUIState };
              } catch (err) {
                console.error('[AgentState] Error getting UI state from new handlers after poll:', err);
              }
            }
          }

          console.log('[AgentState] Async loading complete:', polledUIState?.page_type, 'sections:', polledUIState?.sections?.length);
          return { results, newUIState: polledUIState };
        }

        // No navigation - just wait for React state to settle
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Use local registry (no navigation, or navigation handler unavailable)
      const newUIState = elementRegistryRef.current?.computeUIState() || {
        page_type: 'unknown',
        timestamp: new Date().toISOString(),
        sections: [],
      };

      console.log('[AgentState] Fallback UI state:', newUIState?.page_type, 'sections:', newUIState?.sections?.length);
      return { results, newUIState };
    },
    []
  );

  /**
   * Highlight element for explanatory purposes (called by backend platform_highlight_element tool)
   * Uses longer duration and pulse animation for better visibility
   */
  const highlightElementForExplanation = useCallback((elementId: string) => {
    const highlightManager = highlightManagerRef.current;
    if (!highlightManager) return;

    const durationMs = 3500; // Longer duration for explanations
    highlightManager.highlight(elementId, durationMs);
    setHighlightedElements((prev) => new Set(prev).add(elementId));

    setTimeout(() => {
      setHighlightedElements((prev) => {
        const next = new Set(prev);
        next.delete(elementId);
        return next;
      });
    }, durationMs);
  }, []);

  /**
   * UI control handlers to pass to usePlatformAssistantChat
   */
  const uiControlHandlers: UIControlHandlers = useMemo(
    () => ({
      onGetUIState: getUIState,
      onExecuteAction: executeActions,
      onHighlightElement: highlightElementForExplanation,
    }),
    [getUIState, executeActions, highlightElementForExplanation]
  );

  /**
   * Register handlers with global registry so PlatformAssistant can access them.
   * This bridges the gap between page-level context and global layout component.
   */
  useEffect(() => {
    UIControlHandlerRegistry.register(uiControlHandlers);

    return () => {
      UIControlHandlerRegistry.unregister();
    };
  }, [uiControlHandlers]);

  /**
   * Set connection state - called by parent when voice chat connects/disconnects
   */
  const setConnected = useCallback((connected: boolean) => {
    setIsConnected(connected);
  }, []);

  // Context methods
  const registerElement = useCallback((registration: ElementRegistration) => {
    elementRegistryRef.current?.register(registration);
  }, []);

  const unregisterElement = useCallback((elementId: string) => {
    elementRegistryRef.current?.unregister(elementId);
  }, []);

  const registerAction = useCallback((registration: ActionRegistration) => {
    actionRegistryRef.current?.register(registration);
  }, []);

  const unregisterAction = useCallback((actionId: string) => {
    actionRegistryRef.current?.unregister(actionId);
  }, []);

  const highlightElement = useCallback(
    (elementId: string, durationMs = 2000) => {
      highlightManagerRef.current?.highlight(elementId, durationMs);
      setHighlightedElements((prev) => new Set(prev).add(elementId));

      setTimeout(() => {
        setHighlightedElements((prev) => {
          const next = new Set(prev);
          next.delete(elementId);
          return next;
        });
      }, durationMs);
    },
    []
  );

  const clearHighlights = useCallback(() => {
    highlightManagerRef.current?.clearAllHighlights();
    setHighlightedElements(new Set());
  }, []);

  const isHighlighted = useCallback(
    (elementId: string) => highlightedElements.has(elementId),
    [highlightedElements]
  );

  // Context value
  const value: AgentStateContextValueExtended = {
    isConnected,
    pageContext,
    registerElement,
    unregisterElement,
    registerAction,
    unregisterAction,
    highlightElement,
    clearHighlights,
    isHighlighted,
    uiControlHandlers,
  };

  return (
    <AgentStateContext.Provider value={value}>
      {children}
    </AgentStateContext.Provider>
  );
}

// ============================================
// HOOKS
// ============================================

/**
 * Use the AgentStateContext
 *
 * @throws Error if used outside of AgentStateProvider
 */
export function useAgentStateContext(): AgentStateContextValueExtended {
  const context = useContext(AgentStateContext);
  if (!context) {
    throw new Error(
      'useAgentStateContext must be used within an AgentStateProvider'
    );
  }
  return context;
}

/**
 * Optional hook that returns null if outside provider
 */
export function useAgentStateContextOptional(): AgentStateContextValueExtended | null {
  return useContext(AgentStateContext);
}
